// ─── NEW NAV ──────────────────────────────────────────────────────────────────
// NavBar replaces the original Header. To revert, remove the two lines below
// and uncomment the "ORIGINAL HEADER" block at the bottom of this file.
import NavBar from "./NavBar";
function Header({ variant }) { return <NavBar variant={variant} />; }
export default Header;

/* ─── ORIGINAL HEADER (preserved for easy revert) ─────────────────────────────

//React
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

//Actions
import { listYears } from "../../../actions/yearActions";
import { listWeeks } from "../../../actions/weekActions";
import { listDays } from "../../../actions/dayActions";
import { getUserDetails, logout } from "../../../actions/userActions";

//Internal Components
import {
  ProfileIcon,
  YearIcon,
  WeekIcon,
  DiaryIcon,
  InsightsIcon,
  LogoutIcon,
  LoginIcon,
  RegisterIcon,
  ValuesIcon,
} from "../../../components/ui/Icons/Icon";
import IconBlock from "./components/IconBlock";

//External Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

// Custom Hooks
import useResponsiveState from "../../../hooks/useResponsiveState";

//Styling
import "./header.css";
import DonationBanner from "./DonationBanner";

function Header({ variant, active }) {
  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { id } = useParams();

  const { data, viewType } = useResponsiveState();

  // Redux State Management
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const yearListState = useSelector((state) => state.yearList);
  const { loading: yearLoading, error: yearError, years } = yearListState;

  const weekListState = useSelector((state) => state.weekList);
  const { loading: weekLoading, error: weekError, weeks } = weekListState;

  // You can still derive the specific IDs for use later
  const latestYearId = years?.at(-1)?._id;
  const latestWeekId = weeks?.at(-1)?._id;

  const dayListState = useSelector((state) => state.dayList);
  const { loading: dayLoading, error: dayError, days } = dayListState;

  useEffect(() => {
    dispatch(listDays());
    dispatch(listYears());
    dispatch(listWeeks());
  }, [dispatch, userInfo]);

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  // State
  const [userStatus, setUserStatus] = useState();
  const [menuState, setMenuState] = useState("menu");
  const [trialDays, setTrialDays] = useState();
  const [pageLoading, setPageLoading] = useState();

  const logoutHandler = () => {
    dispatch(logout());
    navigate("/");
  };

  const yearMap = yearError
    ? "#"
    : latestYearId
      ? `/year/${latestYearId}`
      : `/year/create`;

  const weeksMap = weekError
    ? "#"
    : latestWeekId
      ? `/week/${latestWeekId}`
      : `/week/create`;
  const dayUrl =
    days?.length > 0
      ? days[days.length - 1].endScore === undefined
        ? `/today/${days[days.length - 1].id}`
        : `/today`
      : `/today`;

  // Add this logic after your useSelector hooks
  const isDataLoading = yearLoading || weekLoading || dayLoading; // 'loading' is from dayList

  const search = {
    prefix: "fas",
    iconName: "magnifying-glass",
    icon: [
      512,
      512,
      [128269, "search"],
      "f002",
      "M500.3 443.7l-119.7-119.7c27.22-40.41 40.65-90.9 33.46-144.7C401.8 87.79 326.8 13.32 235.2 1.723C99.01-15.51-15.51 99.01 1.724 235.2c11.6 91.64 86.08 166.7 177.6 178.9c53.8 7.189 104.3-6.236 144.7-33.46l119.7 119.7c15.62 15.62 40.95 15.62 56.57 0C515.9 484.7 515.9 459.3 500.3 443.7zM79.1 208c0-70.58 57.42-128 128-128s128 57.42 128 128c0 70.58-57.42 128-128 128S79.1 278.6 79.1 208z",
    ],
  };

  let donate = {
    prefix: "fas",
    iconName: "hand-holding-medical",
    icon: [
      576,
      512,
      [],
      "e05c",
      "M568.2 336.3c-13.12-17.81-38.14-21.66-55.93-8.469l-119.7 88.17h-120.6c-8.748 0-15.1-7.25-15.1-15.99c0-8.75 7.25-16 15.1-16h78.25c15.1 0 30.75-10.88 33.37-26.62c3.25-20-12.12-37.38-31.62-37.38H191.1c-26.1 0-53.12 9.25-74.12 26.25l-46.5 37.74L15.1 383.1C7.251 383.1 0 391.3 0 400v95.98C0 504.8 7.251 512 15.1 512h346.1c22.03 0 43.92-7.188 61.7-20.27l135.1-99.52C577.5 379.1 581.3 354.1 568.2 336.3zM160 176h64v64C224 248.8 231.2 256 240 256h64C312.8 256 320 248.8 320 240v-64h64c8.836 0 16-7.164 16-16V96c0-8.838-7.164-16-16-16h-64v-64C320 7.162 312.8 0 304 0h-64C231.2 0 224 7.162 224 16v64H160C151.2 80 144 87.16 144 96v64C144 168.8 151.2 176 160 176z",
    ],
  };

  const svgIcons = [
    { Component: YearIcon, title: "Map", url: yearMap },
    { Component: WeekIcon, title: "Planner", url: weeksMap },
    { Component: DiaryIcon, title: "Journal", url: dayUrl },
    { Component: InsightsIcon, title: "Insights", url: "/insights" },
    { Component: ProfileIcon, title: "My Account", url: "/my-account/menu" },
    { Component: search, title: "Search", url: "/my-account/menu" },
    {
      Component: donate,
      title: "Donate",
      url: "/donate",
    },
    { Component: LogoutIcon, title: "Logout", url: "" },
    { Component: RegisterIcon, title: "Register", url: "/register" },
    { Component: LoginIcon, title: "Login", url: "/login" },
  ];

  const svgSearch = [
    { Component: search, title: "Search", url: "/my-account/menu" },
  ];

  const [nav, setNav] = useState(false);

  const handleNavChange = () => {
    setNav(!nav);
  };

  const [searchActive, setSearchActive] = useState(false);

  const handleSearchChange = () => {
    if (days?.length === 0) {
      setSearchActive(false);
    } else {
      setSearchActive((prevState) => !prevState);
    }
  };

  const handleNavigation = (url) => {
    navigate(url);
  };

  const renderIconBlock = () => {
    if (!userInfo) {
      return (
        <IconBlock
          block={svgIcons.slice(-2)}
          variant={variant}
          trialDays={trialDays}
          userStatus={userStatus}
          handleNavigation={handleNavigation}
        />
      );
    } else if (searchActive && viewType === "mobile") {
      return (
        <IconBlock
          block={svgSearch}
          variant={variant}
          trialDays={trialDays}
          userStatus={userStatus}
          searchActive={searchActive}
          handleNavigation={handleNavigation}
          handleSearchChange={handleSearchChange}
          data={days}
        />
      );
    } else {
      return (
        <IconBlock
          block={svgIcons.slice(0, -3)}
          variant={variant}
          trialDays={trialDays}
          userStatus={userStatus}
          searchActive={searchActive}
          handleNavigation={handleNavigation}
          handleSearchChange={handleSearchChange}
          data={days}
        />
      );
    }
  };

  return (
    <>
      <nav
        className={variant === "dark" ? `nav__wrapper dark` : `nav__wrapper`}
      >
        <DonationBanner />
        <div
          className={active ? "nav--thin__active" : "nav--thin"}
          onClick={handleNavChange}
        >
          {nav ? (
            <FontAwesomeIcon icon={faChevronDown} className="nav--chevron" />
          ) : (
            <FontAwesomeIcon icon={faChevronUp} className="nav--chevron" />
          )}
        </div>
        {!active ? (
          <div className="nav--wide">{renderIconBlock()}</div>
        ) : nav ? (
          <div className="nav--wide">{renderIconBlock()}</div>
        ) : (
          <></>
        )}
      </nav>
    </>
  );
}

export default Header;
─────────────────────────────────────────────────────────────────────────────── */
